import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { BackButton } from '../common/BackButton';
import TelegramLoginButton from './TelegramLoginButton';
import { telegramService } from '../../services/telegramService';
import { extractErrorMessage } from '../../utils/errorUtils';
import type { TelegramLinkStatus, TelegramLoginData } from '../../types/telegram';

export default function TelegramPage() {
  const navigate = useNavigate();

  const [status, setStatus] = useState<TelegramLinkStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const linkStatus = await telegramService.getLinkStatus();
        if (!cancelled) {
          setStatus(linkStatus);
        }
      } catch (err) {
        console.error('Failed to load Telegram link status:', err);
        if (!cancelled) {
          setError(extractErrorMessage(err, 'Не вдалося завантажити стан підключення Telegram'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAuth = useCallback(async (loginData: TelegramLoginData) => {
    setSaving(true);
    setError(null);
    try {
      setStatus(await telegramService.link(loginData));
    } catch (err) {
      setError(extractErrorMessage(err, 'Не вдалося підключити Telegram'));
    } finally {
      setSaving(false);
    }
  }, []);

  const handleUnlink = async () => {
    setSaving(true);
    setError(null);
    try {
      await telegramService.unlink();
      setStatus(await telegramService.getLinkStatus());
    } catch (err) {
      setError(extractErrorMessage(err, 'Не вдалося відключити Telegram'));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const renderContent = () => {
    if (loading) {
      return <CircularProgress size={24} />;
    }

    if (!status) {
      return null;
    }

    if (!status.enabled) {
      return (
        <Alert severity="info">
          Інтеграція з Telegram не налаштована на цьому сервері.
        </Alert>
      );
    }

    if (status.linked) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <Typography>
            Telegram підключено
            {status.telegramUsername ? ` (@${status.telegramUsername})` : ''}.
            Бот: @{status.botUsername}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={handleUnlink}
            disabled={saving}
          >
            Відключити
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
        <Typography color="text.secondary">
          Підключіть свій Telegram, щоб працювати з CRM через бота @{status.botUsername}.
        </Typography>
        {status.botUsername && (
          <TelegramLoginButton
            botUsername={status.botUsername}
            onAuth={handleAuth}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <BackButton
        label="На головну"
        onClick={handleBack}
      />
      <Container
        maxWidth="sm"
        disableGutters
      >
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant="h6"
            component="h1"
          >
            Telegram
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {renderContent()}
        </Paper>
      </Container>
    </Box>
  );
}
